import {
    ContentButton,
    ExpandableContent,
    formatAddress,
    formatCurrency,
    getProposalNameDisplay,
    LoadStatus,
    PortalDrawer,
    PortalUrls,
    usePortalMode,
} from '@servicetitan/cp-ui';
import {
    BodyText,
    Card,
    Divider,
    Eyebrow,
    Headline,
    Spinner,
    Stack,
} from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { ReactElement, useEffect, useMemo } from 'react';
import { EstimateModel } from '../../../../api/portal-dashboard.api';
import { SettingsStore } from '../../../../api/settings.store';
import { EstimateContainerStore } from '../../../../stores/estimate-container.store';
import { EstimatesFiltersStore } from '../../../../stores/estimates-filters.store';
import { ProposalDrawerStore } from '../../../../stores/proposal-drawer.store';
import { StatusEstimate } from '../status/status';
import * as Styles from './proposal-drawer.module.less';

interface DrawerEstimateProps {
    estimate: EstimateModel;
    showTotalPrice: boolean;
}

const DrawerEstimate = ({ estimate, showTotalPrice }: DrawerEstimateProps) => {
    const summaryHtml = useMemo(
        () => estimate.summary.replaceAll('\n', '<br/>'),
        [estimate.summary]
    );

    return (
        <Card className="m-b-1">
            {estimate.optionColorHex ? (
                <Card.Section
                    className={Styles.estimateHeader}
                    style={{ backgroundColor: estimate.optionColorHex }}
                >
                    <Eyebrow className={Styles.estimateHeaderText}>{estimate.optionName}</Eyebrow>
                </Card.Section>
            ) : (
                <Card.Section blue />
            )}
            <Card.Section className={Styles.estimateContent}>
                <Headline size="small" className="m-b-2">
                    {estimate.name}
                </Headline>
                <StatusEstimate status={estimate.status} />
                <ExpandableContent
                    className="m-t-1"
                    moreBlock
                    html
                    moreText="Show more"
                    content={summaryHtml}
                    rows={6}
                    size="small"
                />
            </Card.Section>
            {showTotalPrice && (
                <Card.Section className={Styles.estimateFooter}>
                    <Eyebrow>Total price</Eyebrow>
                    <Headline>{formatCurrency(estimate.totalPrice)}</Headline>
                </Card.Section>
            )}
        </Card>
    );
};

export const ProposalDrawer = provide({ singletons: [EstimateContainerStore] })(
    observer(() => {
        const [drawerStore, estimateContainerStore, estimatesFiltersStore, settingsStore] =
            useDependencies(
                ProposalDrawerStore,
                EstimateContainerStore,
                EstimatesFiltersStore,
                SettingsStore
            );

        const mode = usePortalMode();

        useEffect(() => {
            estimateContainerStore.init(drawerStore.proposalId);
        }, [drawerStore.proposalId, estimateContainerStore]);

        const enabledEstimateStatuses = estimatesFiltersStore.getEnabledEstimateStatuses();
        const estimates = (estimateContainerStore.item?.estimates ?? []).filter(estimate =>
            enabledEstimateStatuses.includes(estimate.status)
        );
        const estimatesCount = estimates.length;

        let content: ReactElement | undefined;

        const proposalNameDisplay = getProposalNameDisplay(
            estimateContainerStore.item?.name,
            estimateContainerStore.item?.opportunityId,
            estimateContainerStore.item?.serviceLocation?.address
                ? formatAddress(estimateContainerStore.item?.serviceLocation?.address)
                : undefined,
            estimateContainerStore.item?.serviceLocation?.name,
            settingsStore.proposalNameDisplayOptions
        );

        switch (estimateContainerStore.status) {
            case LoadStatus.Loading:
                content = <Spinner />;
                break;
            case LoadStatus.Error:
                content = (
                    <BodyText>
                        Error loading {drawerStore.isContract ? 'contract' : 'proposal'}
                    </BodyText>
                );
                break;
            case LoadStatus.Ok:
                content = (
                    <Stack direction="column">
                        <Headline size="small" className="m-b-0">
                            {estimatesCount === 1 ? '1 Estimate' : `${estimatesCount} Estimates`}
                        </Headline>
                        <Divider spacing={2} />
                        {estimates.map(e => (
                            <DrawerEstimate
                                key={e.id}
                                estimate={e}
                                showTotalPrice={
                                    false
                                } /* temporary providing false VS removing the code, since we're going to implement new show/hide pricing logic */
                            />
                        ))}
                    </Stack>
                );
                break;
            default:
                content = undefined;
                break;
        }

        const drawerHeader = (
            <Stack className="w-100" direction="row" justifyContent="space-between">
                <Headline>{proposalNameDisplay ?? 'Loading...'}</Headline>
                <ContentButton
                    small
                    outline
                    href={`${PortalUrls.proposals.root}/${drawerStore.proposalId}`}
                >
                    {drawerStore.isContract ? 'View Contract' : 'View Proposal'}
                </ContentButton>
            </Stack>
        );

        return (
            <PortalDrawer
                mobile={mode.mobile}
                open={drawerStore.isOpen}
                title=""
                drawer={{ header: drawerHeader }}
                onClose={drawerStore.close}
            >
                {content}
            </PortalDrawer>
        );
    })
);
